import { AccountHeader } from '@/components/AccountHeader';
import { AppFooter } from '@/components/AppFooter';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, Calendar, MapPin, CreditCard, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getOrderById, Order } from '@/services/order.service';
import { useToast } from '@/hooks/use-toast';

const OrderDetails = () => {
  const { user, isAuthenticated, isInitializing } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Wait for auth initialization to complete before checking authentication
    if (isInitializing) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (id) {
      fetchOrderDetails(Number(id));
    }
  }, [isAuthenticated, isInitializing, navigate, id]);

  const fetchOrderDetails = async (orderId: number) => {
    try {
      setIsLoading(true);
      const orderData = await getOrderById(orderId);
      setOrder(orderData);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load order details. Please try again later.',
      });
      navigate('/orders');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('pending')) return 'bg-yellow-100 text-yellow-800';
    if (statusLower.includes('processing') || statusLower.includes('confirmed')) return 'bg-blue-100 text-blue-800';
    if (statusLower.includes('shipped')) return 'bg-purple-100 text-purple-800';
    if (statusLower.includes('delivered') || statusLower.includes('completed')) return 'bg-green-100 text-green-800';
    if (statusLower.includes('cancelled') || statusLower.includes('canceled')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatAddress = (address: Order['shipping_address']) => {
    if (!address) return 'Address not available';
    
    const address1 = Array.isArray(address.address1) 
      ? address.address1.filter(Boolean).join(', ') 
      : address.address1 || '';
    
    const parts = [
      `${address.first_name || ''} ${address.last_name || ''}`.trim(),
      address1,
      address.address2,
      address.city,
      address.state,
      address.postcode,
      address.country_name || address.country,
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Show loading state while initializing auth
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AccountHeader />
        <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AccountHeader />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/orders')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !order ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">Order not found</h3>
              <p className="text-muted-foreground mb-4">The order you're looking for doesn't exist.</p>
              <Button onClick={() => navigate('/orders')}>Back to Orders</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Order Header */}
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-2xl">
                      <Package className="h-6 w-6" />
                      Order #{order.increment_id || order.id}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Placed on {formatDate(order.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-col items-start md:items-end gap-2">
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      {order.status_label || order.status}
                    </span>
                    <span className="text-2xl font-bold">{order.formated_grand_total || `₹${parseFloat(order.grand_total || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Order Items */}
            <Card>
              <CardHeader>
                <CardTitle>Order Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item, index) => {
                      const productImage = item.product?.base_image?.small_image_url 
                        || item.product?.base_image?.medium_image_url
                        || item.product?.images?.[0]?.small_image_url
                        || item.product?.images?.[0]?.url;
                      
                      return (
                        <div key={item.id || index} className="flex items-start gap-4 p-4 bg-muted rounded-lg border">
                          {productImage && (
                            <img 
                              src={productImage} 
                              alt={item.name} 
                              className="w-20 h-20 md:w-24 md:h-24 object-cover rounded border"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-base mb-1">{item.name || 'Product'}</p>
                            {item.sku && (
                              <p className="text-sm text-muted-foreground mb-1">SKU: {item.sku}</p>
                            )}
                            <div className="flex flex-wrap gap-4 mt-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">Quantity: </span>
                                <span className="font-medium">{item.qty_ordered || 1}</span>
                              </div>
                              {item.product?.formated_price && (
                                <div>
                                  <span className="text-muted-foreground">Unit Price: </span>
                                  <span className="font-medium">{item.product.formated_price}</span>
                                </div>
                              )}
                              {parseFloat(item.tax_amount || '0') > 0 && (
                                <div>
                                  <span className="text-muted-foreground">Tax (GST): </span>
                                  <span className="font-medium">{item.formated_tax_amount || `₹${parseFloat(item.tax_amount || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                                </div>
                              )}
                              {parseFloat(item.discount_amount || '0') > 0 && (
                                <div>
                                  <span className="text-muted-foreground">Discount: </span>
                                  <span className="font-medium text-green-600">-{item.formated_discount_amount || `₹${parseFloat(item.discount_amount || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">{item.formated_total || `₹${parseFloat(item.total || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</p>
                            {item.formated_base_total && parseFloat(item.base_total || '0') < parseFloat(item.total || '0') && (
                              <p className="text-xs line-through text-muted-foreground mt-1">
                                {item.formated_base_total}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No items found</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Shipping Address */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Shipping Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {order.shipping_address ? (
                    <div className="text-sm space-y-2">
                      <p className="font-semibold text-base">
                        {order.shipping_address.first_name} {order.shipping_address.last_name}
                      </p>
                      <p className="text-muted-foreground">{formatAddress(order.shipping_address)}</p>
                      {order.shipping_address.phone && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Phone: </span>
                          {order.shipping_address.phone}
                        </p>
                      )}
                      {order.shipping_address.email && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Email: </span>
                          {order.shipping_address.email}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Address not available</p>
                  )}
                </CardContent>
              </Card>

              {/* Billing Address */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Billing Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {order.billing_address ? (
                    <div className="text-sm space-y-2">
                      <p className="font-semibold text-base">
                        {order.billing_address.first_name} {order.billing_address.last_name}
                      </p>
                      <p className="text-muted-foreground">{formatAddress(order.billing_address)}</p>
                      {order.billing_address.phone && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Phone: </span>
                          {order.billing_address.phone}
                        </p>
                      )}
                      {order.billing_address.email && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Email: </span>
                          {order.billing_address.email}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Address not available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Payment Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-base">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-medium">{order.formated_sub_total || `₹${parseFloat(order.sub_total || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                  </div>
                  {parseFloat(order.tax_amount || '0') > 0 && (
                    <div className="flex justify-between text-base">
                      <span className="text-muted-foreground">Tax (GST):</span>
                      <span className="font-medium">{order.formated_tax_amount || `₹${parseFloat(order.tax_amount || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                    </div>
                  )}
                  {parseFloat(order.discount_amount || '0') > 0 && (
                    <div className="flex justify-between text-base text-green-600">
                      <span className="text-muted-foreground">Discount:</span>
                      <span className="font-medium">-{order.formated_discount_amount || `₹${parseFloat(order.discount_amount || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                    </div>
                  )}
                  {parseFloat(order.shipping_amount || '0') > 0 && (
                    <div className="flex justify-between text-base">
                      <span className="text-muted-foreground">Shipping:</span>
                      <span className="font-medium">{order.formated_shipping_amount || `₹${parseFloat(order.shipping_amount || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t text-lg font-semibold">
                    <span>Total:</span>
                    <span>{order.formated_grand_total || `₹${parseFloat(order.grand_total || '0').toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                    <div>
                      <span className="text-sm text-muted-foreground">Payment Method:</span>
                      <p className="font-medium">{order.payment_title || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Shipping Method:</span>
                      <p className="font-medium">{order.shipping_title || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      <AppFooter />
    </div>
  );
};

export default OrderDetails;

